﻿/**
 * DUEL Program â€” Setup Screen
 *
 * Pro-gated. Guides athlete through profile setup and generates
 * a deterministic weekly training plan.
 *
 * Doctrine: AGENTS.md â€” no competitive engine changes, no migrations.
 */

import { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { generateWeekPlan } from '@/lib/coachEngine';
import { saveCoachProfile, saveCoachPlan, loadCoachProfile } from '@/lib/coachStorage';
import type {
  CoachGoal,
  EquipmentLevel,
  ExperienceLevel,
  LiftEmphasis,
  BodyEmphasis,
  CoachProfile,
} from '@/lib/coachEngine';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE, CARD } from '@/constants/designTokens';
import { ArrowLeft, Lock, Dumbbell, Zap, Target, Activity } from 'lucide-react-native';

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Types & constants
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const GOALS: { id: CoachGoal; label: string; description: string }[] = [
  { id: 'strength', label: 'STRENGTH', description: 'Build maximum force. Low reps, heavy loads, compound focus.' },
  { id: 'hypertrophy', label: 'HYPERTROPHY', description: 'Maximize muscle size. Volume, tension, and time under load.' },
  { id: 'general_fitness', label: 'GENERAL FITNESS', description: 'Build a capable body. Balanced work across all movement patterns.' },
  { id: 'athletic_performance', label: 'ATHLETIC PERFORMANCE', description: 'Explosive power and compound strength. Sport-ready training.' },
];

const EQUIPMENT_OPTIONS: { id: EquipmentLevel; label: string; description: string }[] = [
  { id: 'full_gym', label: 'FULL GYM', description: 'Barbells, machines, cables, full rack setup' },
  { id: 'basic_gym', label: 'BASIC GYM', description: 'Barbells, dumbbells, pull-up bar' },
  { id: 'home_gym', label: 'HOME GYM', description: 'Dumbbells, pull-up bar, limited setup' },
  { id: 'bodyweight', label: 'BODYWEIGHT', description: 'No equipment, bodyweight movements only' },
];

const EXPERIENCE_OPTIONS: { id: ExperienceLevel; label: string; description: string }[] = [
  { id: 'beginner', label: 'BEGINNER', description: 'Under 1 year of consistent training' },
  { id: 'intermediate', label: 'INTERMEDIATE', description: '1â€“3 years of structured training' },
  { id: 'advanced', label: 'ADVANCED', description: '3+ years of serious, progressive lifting' },
];

const DURATION_OPTIONS: Array<{ value: 30 | 45 | 60 | 75 | 90; label: string }> = [
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
  { value: 75, label: '75 min' },
  { value: 90, label: '90 min' },
];

const DAYS_OPTIONS: Array<2 | 3 | 4 | 5 | 6> = [2, 3, 4, 5, 6];

const LIFT_EMPHASIS_OPTIONS: Array<{ id: LiftEmphasis; label: string }> = [
  { id: 'balanced', label: 'BALANCED' },
  { id: 'squat', label: 'SQUAT' },
  { id: 'bench', label: 'BENCH' },
  { id: 'deadlift', label: 'DEADLIFT' },
  { id: 'pull_up', label: 'PULL-UP' },
  { id: 'farmers_carry', label: "FARMER'S CARRY" },
];

const BODY_EMPHASIS_OPTIONS: Array<{ id: BodyEmphasis; label: string }> = [
  { id: 'full_body', label: 'FULL BODY' },
  { id: 'upper', label: 'UPPER' },
  { id: 'lower', label: 'LOWER' },
];

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Screen
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export default function DuelCoachScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const { profile } = useAuth();
  const { tier } = useSubscription();
  const c = theme.colors;

  const isPro = hasAtLeast(tier, 'pro');

  const [goal, setGoal] = useState<CoachGoal>('strength');
  const [daysPerWeek, setDaysPerWeek] = useState<2 | 3 | 4 | 5 | 6>(4);
  const [equipment, setEquipment] = useState<EquipmentLevel>('full_gym');
  const [experience, setExperience] = useState<ExperienceLevel>('intermediate');
  const [durationMinutes, setDurationMinutes] = useState<30 | 45 | 60 | 75 | 90>(60);
  const [liftEmphasis, setLiftEmphasis] = useState<LiftEmphasis>('balanced');
  const [bodyEmphasis, setBodyEmphasis] = useState<BodyEmphasis>('full_body');
  const [generating, setGenerating] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [weekNumber, setWeekNumber] = useState(1);

  // Load existing profile if present
  useEffect(() => {
    if (!profile?.id) { setLoadingExisting(false); return; }
    loadCoachProfile(profile.id).then((saved) => {
      if (saved) {
        setGoal(saved.goal);
        setDaysPerWeek(saved.daysPerWeek);
        setEquipment(saved.equipment);
        setExperience(saved.experience);
        setDurationMinutes(saved.durationMinutes);
        if (saved.liftEmphasis) setLiftEmphasis(saved.liftEmphasis);
        if (saved.bodyEmphasis) setBodyEmphasis(saved.bodyEmphasis);
        setWeekNumber((saved.weekNumber ?? 0) + 1);
      }
      setLoadingExisting(false);
    });
  }, [profile?.id]);

  const handleGenerate = async () => {
    if (!profile?.id) return;
    setGenerating(true);
    try {
      const coachProfile: CoachProfile = {
        goal,
        daysPerWeek,
        equipment,
        experience,
        durationMinutes,
        liftEmphasis,
        bodyEmphasis,
        weekNumber,
      };
      await saveCoachProfile(profile.id, coachProfile);
      const plan = generateWeekPlan(coachProfile);
      await saveCoachPlan(profile.id, plan);
      router.push('/duel-coach-plan' as any);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (Platform.OS === 'web') {
        alert(`Error: ${msg}`);
      } else {
        Alert.alert('Error', msg);
      }
    } finally {
      setGenerating(false);
    }
  };

  // â”€â”€ Pro gate â”€â”€
  if (!isPro) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <View style={[styles.header, { borderBottomColor: c.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={styles.backBtn} accessibilityRole="button" accessibilityLabel="Go back">
            <ArrowLeft size={22} color={c.text} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: c.text }]}>DUEL Program</Text>
          <View style={{ width: 40 }} />
        </View>
        <View style={styles.gateContainer}>
          <Lock size={48} color={c.accent} />
          <Text style={[styles.gateTitle, { color: c.text }]}>PRO REQUIRED</Text>
          <Text style={[styles.gateBody, { color: c.textSecondary }]}>
            DUEL Program generates a personalized weekly training plan based on your goals, equipment, and experience. Upgrade to Pro to unlock it.
          </Text>
          <Text style={[styles.gateSub, { color: c.textTertiary }]}>
            Competitive rankings and matchmaking are unaffected by subscription tier.
          </Text>
          <TouchableOpacity
            style={[styles.upgradeBtn, { backgroundColor: c.accent }]}
            onPress={() => router.push('/paywall' as any)}
            accessibilityRole="button"
            accessibilityLabel="Upgrade to Pro"
          >
            <Text style={[styles.upgradeBtnText, { color: c.background }]}>UPGRADE TO PRO</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  if (loadingExisting) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn} accessibilityRole="button" accessibilityLabel="Go back">
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]}>DUEL Program</Text>
        <View style={{ width: 40 }} />
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.container}
        showsVerticalScrollIndicator={false}
      >
        {/* Week context */}
        <View style={[styles.weekBadge, { backgroundColor: c.accent + '20', borderColor: c.accent + '40' }]}>
          <Zap size={14} color={c.accent} />
          <Text style={[styles.weekBadgeText, { color: c.accent }]}>
            WEEK {weekNumber} â€” {weekNumber % 4 === 0 ? 'DELOAD' : `MESOCYCLE ${Math.ceil(weekNumber / 4)}`}
          </Text>
        </View>

        {/* â”€â”€ Goal â”€â”€ */}
        <SectionHeader icon={<Target size={16} color={c.accent} />} label="TRAINING GOAL" colors={c} />
        {GOALS.map((g) => (
          <SelectCard
            key={g.id}
            selected={goal === g.id}
            label={g.label}
            description={g.description}
            onPress={() => setGoal(g.id)}
            colors={c}
          />
        ))}

        {/* â”€â”€ Days per week â”€â”€ */}
        <SectionHeader icon={<Activity size={16} color={c.accent} />} label="DAYS PER WEEK" colors={c} />
        <View style={styles.pillRow}>
          {DAYS_OPTIONS.map((d) => (
            <TouchableOpacity
              key={d}
              style={[
                styles.pill,
                {
                  backgroundColor: daysPerWeek === d ? c.accent : c.surface,
                  borderColor: daysPerWeek === d ? c.accent : c.border,
                },
              ]}
              onPress={() => setDaysPerWeek(d)}
              accessibilityRole="button"
              accessibilityLabel={`${d} days per week`}
            >
              <Text style={[styles.pillText, { color: daysPerWeek === d ? c.background : c.text }]}>
                {d}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* â”€â”€ Equipment â”€â”€ */}
        <SectionHeader icon={<Dumbbell size={16} color={c.accent} />} label="AVAILABLE EQUIPMENT" colors={c} />
        {EQUIPMENT_OPTIONS.map((eq) => (
          <SelectCard
            key={eq.id}
            selected={equipment === eq.id}
            label={eq.label}
            description={eq.description}
            onPress={() => setEquipment(eq.id)}
            colors={c}
          />
        ))}

        {/* â”€â”€ Experience â”€â”€ */}
        <SectionHeader icon={<Zap size={16} color={c.accent} />} label="EXPERIENCE LEVEL" colors={c} />
        {EXPERIENCE_OPTIONS.map((ex) => (
          <SelectCard
            key={ex.id}
            selected={experience === ex.id}
            label={ex.label}
            description={ex.description}
            onPress={() => setExperience(ex.id)}
            colors={c}
          />
        ))}

        {/* â”€â”€ Duration â”€â”€ */}
        <SectionHeader icon={<Activity size={16} color={c.accent} />} label="SESSION DURATION" colors={c} />
        <View style={styles.pillRow}>
          {DURATION_OPTIONS.map((d) => (
            <TouchableOpacity
              key={d.value}
              style={[
                styles.pill,
                styles.pillWide,
                {
                  backgroundColor: durationMinutes === d.value ? c.accent : c.surface,
                  borderColor: durationMinutes === d.value ? c.accent : c.border,
                },
              ]}
              onPress={() => setDurationMinutes(d.value)}
              accessibilityRole="button"
              accessibilityLabel={d.label}
            >
              <Text style={[styles.pillText, { color: durationMinutes === d.value ? c.background : c.text }]}>
                {d.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* â”€â”€ Lift Emphasis (optional) â”€â”€ */}
        <SectionHeader icon={<Target size={16} color={c.accent} />} label="LIFT EMPHASIS (OPTIONAL)" colors={c} />
        <View style={styles.pillRow}>
          {LIFT_EMPHASIS_OPTIONS.map((l) => (
            <TouchableOpacity
              key={l.id}
              style={[
                styles.pill,
                styles.pillWide,
                {
                  backgroundColor: liftEmphasis === l.id ? c.accent : c.surface,
                  borderColor: liftEmphasis === l.id ? c.accent : c.border,
                },
              ]}
              onPress={() => setLiftEmphasis(l.id)}
              accessibilityRole="button"
              accessibilityLabel={l.label}
            >
              <Text style={[styles.pillText, { color: liftEmphasis === l.id ? c.background : c.text }]}>
                {l.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* â”€â”€ Body Emphasis (optional) â”€â”€ */}
        <SectionHeader icon={<Target size={16} color={c.accent} />} label="BODY EMPHASIS (OPTIONAL)" colors={c} />
        <View style={styles.pillRow}>
          {BODY_EMPHASIS_OPTIONS.map((b) => (
            <TouchableOpacity
              key={b.id}
              style={[
                styles.pill,
                styles.pillWide,
                {
                  backgroundColor: bodyEmphasis === b.id ? c.accent : c.surface,
                  borderColor: bodyEmphasis === b.id ? c.accent : c.border,
                },
              ]}
              onPress={() => setBodyEmphasis(b.id)}
              accessibilityRole="button"
              accessibilityLabel={b.label}
            >
              <Text style={[styles.pillText, { color: bodyEmphasis === b.id ? c.background : c.text }]}>
                {b.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* â”€â”€ Generate â”€â”€ */}
        <TouchableOpacity
          style={[styles.generateBtn, { backgroundColor: c.accent, opacity: generating ? 0.7 : 1 }]}
          onPress={handleGenerate}
          disabled={generating}
          accessibilityRole="button"
          accessibilityLabel="Generate my plan"
        >
          {generating ? (
            <ActivityIndicator color={c.background} />
          ) : (
            <>
              <Zap size={18} color={c.background} />
              <Text style={[styles.generateBtnText, { color: c.background }]}>GENERATE MY PLAN</Text>
            </>
          )}
        </TouchableOpacity>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Sub-components
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

type Colors = ReturnType<typeof useTheme>['theme']['colors'];

function SectionHeader({ icon, label, colors }: { icon: React.ReactNode; label: string; colors: Colors }) {
  return (
    <View style={sectionHeaderStyles.row}>
      {icon}
      <Text style={[sectionHeaderStyles.label, { color: colors.accent }]}>{label}</Text>
    </View>
  );
}

const sectionHeaderStyles = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: SPACE.xs, marginTop: SPACE.lg, marginBottom: SPACE.sm },
  label: { fontSize: FONT.xs, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.wide, textTransform: 'uppercase' },
});

function SelectCard({
  selected,
  label,
  description,
  onPress,
  colors,
}: {
  selected: boolean;
  label: string;
  description: string;
  onPress: () => void;
  colors: Colors;
}) {
  return (
    <TouchableOpacity
      style={[
        selectCardStyles.card,
        {
          backgroundColor: selected ? colors.accent + '18' : colors.surface,
          borderColor: selected ? colors.accent : colors.border,
        },
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <View style={selectCardStyles.row}>
        <View
          style={[
            selectCardStyles.dot,
            { backgroundColor: selected ? colors.accent : 'transparent', borderColor: selected ? colors.accent : colors.textTertiary },
          ]}
        />
        <View style={{ flex: 1 }}>
          <Text style={[selectCardStyles.label, { color: selected ? colors.accent : colors.text }]}>{label}</Text>
          <Text style={[selectCardStyles.description, { color: colors.textSecondary }]}>{description}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const selectCardStyles = StyleSheet.create({
  card: { borderWidth: 1, borderRadius: RADIUS.xs, padding: SPACE.base, marginBottom: SPACE.sm },
  row: { flexDirection: 'row', alignItems: 'flex-start', gap: SPACE.sm },
  dot: { width: 12, height: 12, borderRadius: 6, borderWidth: 2, marginTop: 3 },
  label: { fontSize: FONT.sm, fontFamily: 'BarlowCondensed_700Bold', letterSpacing: TRACKING.tight, textTransform: 'uppercase', marginBottom: 2 },
  description: { fontSize: FONT.xs, fontFamily: 'Inter_400Regular', lineHeight: 16 },
});

// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// Main styles
// â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: SPACE.lg,
    paddingVertical: SPACE.base,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: FONT.lg,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: TRACKING.wide,
  },
  backBtn: { width: 40, alignItems: 'flex-start' },
  container: {
    paddingHorizontal: SPACE.lg,
    paddingTop: SPACE.md,
    paddingBottom: 64,
  },
  weekBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.xs,
    alignSelf: 'flex-start',
    paddingHorizontal: SPACE.sm,
    paddingVertical: SPACE.xs,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
    marginBottom: SPACE.sm,
  },
  weekBadgeText: {
    fontSize: FONT.xs,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: TRACKING.wide,
  },
  pillRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACE.sm,
    marginBottom: SPACE.sm,
  },
  pill: {
    width: 48,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: RADIUS.xs,
    borderWidth: 1,
  },
  pillWide: {
    width: 'auto',
    paddingHorizontal: SPACE.base,
  },
  pillText: {
    fontSize: FONT.sm,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: TRACKING.tight,
  },
  generateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACE.sm,
    marginTop: SPACE.xl,
    paddingVertical: SPACE.md,
    borderRadius: RADIUS.xs,
  },
  generateBtnText: {
    fontSize: FONT.md,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: TRACKING.wide,
  },

  // Gate
  gateContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: SPACE.xl,
    gap: SPACE.lg,
  },
  gateTitle: {
    fontSize: FONT['2xl'],
    fontFamily: 'Oswald_700Bold',
    letterSpacing: TRACKING.wide,
    textAlign: 'center',
  },
  gateBody: {
    fontSize: FONT.base,
    fontFamily: 'Inter_400Regular',
    textAlign: 'center',
    lineHeight: 22,
  },
  gateSub: {
    fontSize: FONT.sm,
    fontFamily: 'Inter_400Regular',
    textAlign: 'center',
    lineHeight: 18,
  },
  upgradeBtn: {
    paddingVertical: SPACE.base,
    paddingHorizontal: SPACE.xl,
    borderRadius: RADIUS.xs,
    marginTop: SPACE.sm,
  },
  upgradeBtnText: {
    fontSize: FONT.base,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: TRACKING.wide,
  },
});

